/*
 * Red Wing work boot inquiry: dedicated-form mode.
 *
 * Wraps the "Shop In Store" button when the buy_buttons block's
 * red_wing_inquiry setting is "form". If an inline Shopify Forms app block is
 * in the same product section, the button reveals that form instead of opening
 * the mailto link, and the form's Size, Width and "Selected boot" fields are
 * filled from the variant chosen in the page's own variant picker.
 *
 * Shopify Forms has no supported prefill for custom fields, so this reaches
 * into its (open) shadow root and drives the fields the way a customer would.
 * Everything degrades safely: no form block leaves the mailto href in place,
 * and a field or option that can't be matched is left for the customer.
 */

if (!customElements.get('red-wing-inquiry')) {
  customElements.define(
    'red-wing-inquiry',
    class RedWingInquiry extends HTMLElement {
      connectedCallback() {
        this.data = this.readData();
        this.variantId = this.data?.selected ?? null;

        this.formBlock = this.findFormBlock();
        if (!this.formBlock) return;

        this.trigger = this.querySelector('[data-red-wing-trigger]');
        this.onTriggerClick = this.onTriggerClick.bind(this);
        this.trigger?.addEventListener('click', this.onTriggerClick);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event.data.sectionId !== this.dataset.sectionId || !event.data.variant) return;
            this.variantId = event.data.variant.id;
            if (this.isOpen()) this.syncForm();
          });
        }

        // The form renders asynchronously. If the dialog is already open by
        // the time it's ready, fill it then.
        this.whenFormReady((root) => {
          this.formRoot = root;
          this.patchFormStyles(root);
          if (this.isOpen()) this.syncForm();
        });
      }

      disconnectedCallback() {
        this.trigger?.removeEventListener('click', this.onTriggerClick);
        this.unsubscribe?.();
        this.observer?.disconnect();
      }

      readData() {
        const el = this.querySelector('[data-red-wing-variants]');
        if (!el) return null;
        try {
          return JSON.parse(el.textContent);
        } catch (error) {
          return null;
        }
      }

      // The server renders an empty `[data-form-root]` placeholder inside the
      // app block; Shopify Forms mounts <shopify-forms-embed> into it later.
      // Once opened, the block lives in the dialog, so look there first: the
      // buy buttons re-render on variant change and this element reconnects.
      findFormBlock() {
        const scope =
          this.closest('product-info') ||
          document.getElementById(`MainProduct-${this.dataset.sectionId}`) ||
          document;
        const formRoot =
          this.existingDialog()?.querySelector('[data-form-root]') || scope.querySelector('[data-form-root]');
        if (!formRoot) return null;
        return formRoot.closest('.shopify-app-block') || formRoot;
      }

      existingDialog() {
        return document.getElementById(`RedWingInquiry-${this.dataset.sectionId}`);
      }

      // One dialog per product section, appended to <body> so no ancestor's
      // overflow or stacking context can clip it.
      getDialog() {
        let dialog = this.existingDialog();
        if (dialog) return dialog;

        const template = this.querySelector('template[data-red-wing-dialog]');
        if (!template) return null;
        dialog = template.content.firstElementChild.cloneNode(true);
        document.body.appendChild(dialog);

        dialog.querySelector('[data-red-wing-close]')?.addEventListener('click', () => dialog.close());
        // The dialog element itself is the backdrop; the box sits inside it.
        dialog.addEventListener('click', (event) => {
          if (event.target === dialog) dialog.close();
        });
        // Non-modal dialogs don't close on Escape by themselves.
        const onKeydown = (event) => {
          if (event.key === 'Escape' && dialog.open) dialog.close();
        };
        document.addEventListener('keydown', onKeydown);
        dialog.addEventListener('close', () => {
          document.body.classList.remove('overflow-hidden');
          this.trigger?.focus({ preventScroll: true });
        });
        return dialog;
      }

      // Only fill the form while it's on screen: a field in a closed dialog
      // can't take focus, which Forms needs to float its labels.
      isOpen() {
        return !!this.existingDialog()?.open;
      }

      onTriggerClick(event) {
        const dialog = this.getDialog();
        if (!dialog || typeof dialog.show !== 'function') return; // keep the mailto

        event.preventDefault();
        const body = dialog.querySelector('[data-red-wing-dialog-body]');
        if (this.formBlock.parentElement !== body) body.appendChild(this.formBlock);

        // show(), not showModal(): a modal dialog makes the rest of the page
        // inert, which hides Shopify Forms' hCaptcha challenge (appended to
        // <body>) and leaves the submit button spinning forever.
        dialog.show();
        dialog.querySelector('.red-wing-inquiry__box')?.focus({ preventScroll: true });
        document.body.classList.add('overflow-hidden');
        this.syncForm();
      }

      // Waits for the embed to mount, then for its shadow root to render the
      // form. Mutations inside a shadow root don't reach an observer on the
      // light DOM, hence the second observer.
      whenFormReady(callback) {
        const watchShadow = (root) => {
          if (root.querySelector('form')) {
            callback(root);
            return;
          }
          this.observer = new MutationObserver(() => {
            if (!root.querySelector('form')) return;
            this.observer.disconnect();
            callback(root);
          });
          this.observer.observe(root, { childList: true, subtree: true });
        };

        const embed = this.formBlock.querySelector('shopify-forms-embed');
        if (embed?.shadowRoot) {
          watchShadow(embed.shadowRoot);
          return;
        }

        this.observer = new MutationObserver(() => {
          const mounted = this.formBlock.querySelector('shopify-forms-embed');
          if (!mounted?.shadowRoot) return;
          this.observer.disconnect();
          watchShadow(mounted.shadowRoot);
        });
        this.observer.observe(this.formBlock, { childList: true, subtree: true });
      }

      // Forms floats a filled field's label by setting an inline `top` equal to
      // the input's top padding and nudging it up half a line, which leaves
      // the label sitting on the text. Pin floated labels to the top of the
      // field instead. Class names are build-hashed, hence [class*=].
      patchFormStyles(root) {
        if (root.querySelector('style[data-red-wing-patch]')) return;
        const style = document.createElement('style');
        style.dataset.redWingPatch = '';
        style.textContent = `
          [class*="_formFieldContainer_"] label[class*="_inputFilled_"],
          [class*="_formFieldContainer_"]:focus-within label[class*="_formInputFieldLabel_"] {
            top: 0.5rem !important;
            transform: scale(0.85) !important;
          }
        `;
        root.appendChild(style);
      }

      // Size and width for the current variant. Most boots use separate Size
      // and Width options; a few use a single option with values like "7 D".
      currentSizeAndWidth() {
        const values = this.data?.variants?.[this.variantId];
        if (!values) return null;

        const names = this.data.options || [];
        const sizeIndex = names.findIndex((name) => /size/i.test(name));
        const widthIndex = names.findIndex((name) => /width/i.test(name));
        if (sizeIndex !== -1 && widthIndex !== -1) {
          return { size: values[sizeIndex], width: values[widthIndex] };
        }

        const parts = String(values[0] || '')
          .trim()
          .split(/[\s/]+/);
        if (parts.length < 2) return null;
        return { size: parts[0], width: parts[parts.length - 1] };
      }

      async syncForm() {
        if (!this.formRoot || this.syncing) return;
        const current = this.currentSizeAndWidth();
        if (!current) return;

        this.syncing = true;
        try {
          for (const { label, choose } of this.dropdowns()) {
            if (/width/i.test(label)) {
              await choose(current.width);
            } else if (/size/i.test(label) && !/current/i.test(label)) {
              await choose(current.size);
            }
          }

          // "Selected boot" / "Product": Forms has no hidden fields, so the variant ID rides
          // in a read-only text field. Flow takes it with `split: '#' | last`;
          // keep the "· #<id>" suffix if this format changes.
          const summary = `${this.data.title} — ${current.size} / ${current.width} · #${this.variantId}`;
          this.formRoot.querySelectorAll('input[type="text"], input:not([type])').forEach((input) => {
            if (!/^\s*(selected boot|product)\s*$/i.test(this.labelText(input))) return;
            input.readOnly = true;
            this.setNativeValue(input, HTMLInputElement.prototype, summary);
            // Forms only floats a field's label on focus/blur, so without this
            // the label sits on top of the filled-in text.
            input.focus({ preventScroll: true });
            input.blur();
          });
        } finally {
          this.syncing = false;
        }
      }

      // Every dropdown in the form as { label, choose(value) }. Shopify Forms
      // renders its own listbox (a toggle button plus role="option" items with
      // ids like "select-custom#width-EE"); native <select>s are handled too.
      dropdowns() {
        const listboxes = Array.from(
          this.formRoot.querySelectorAll('button[aria-labelledby$="-selector-value"]')
        ).map((toggle) => {
          const [labelId, valueId] = toggle.getAttribute('aria-labelledby').split(/\s+/);
          const fieldId = labelId.replace(/-selector-label$/, '');
          return {
            label: this.formRoot.getElementById(labelId)?.textContent || '',
            choose: (value) => this.chooseListboxOption(toggle, fieldId, valueId, value),
          };
        });

        const selects = Array.from(this.formRoot.querySelectorAll('select')).map((select) => ({
          label: this.labelText(select),
          choose: async (value) => {
            const option = Array.from(select.options).find(
              (opt) => opt.value.trim() === value || opt.textContent.trim() === value
            );
            if (option) this.setNativeValue(select, HTMLSelectElement.prototype, option.value);
          },
        }));

        return [...listboxes, ...selects];
      }

      async chooseListboxOption(toggle, fieldId, valueId, value) {
        const wanted = String(value).trim();
        const shown = () => this.formRoot.getElementById(valueId)?.textContent.trim();
        const isExpanded = () => toggle.getAttribute('aria-expanded') === 'true';
        if (shown() === wanted) return;

        // The list only renders once open, and the form re-renders on its own
        // schedule, so wait for each step instead of assuming a tick is enough.
        if (!isExpanded()) toggle.click();
        const options = await this.waitFor(() => {
          const found = toggle.parentElement.querySelectorAll('[role="option"]');
          return found.length ? Array.from(found) : null;
        });

        const option =
          options?.find((opt) => opt.id === `select-${fieldId}-${wanted}`) ||
          options?.find((opt) => opt.textContent.trim() === wanted);

        if (option) {
          option.click();
          await this.waitFor(() => shown() === wanted);
        }

        // Covers both a missing option (e.g. a width the form doesn't list,
        // left for the customer) and a list that stays open after choosing.
        if (isExpanded()) {
          toggle.click();
          await this.waitFor(() => !isExpanded());
        }
      }

      async waitFor(check, timeout = 1000) {
        const start = performance.now();
        while (performance.now() - start < timeout) {
          const result = check();
          if (result) return result;
          await new Promise((resolve) => setTimeout(resolve, 25));
        }
        return null;
      }

      labelText(field) {
        const labelled = field.labels?.[0]?.textContent;
        if (labelled) return labelled;
        if (field.getAttribute('aria-label')) return field.getAttribute('aria-label');
        const ids = (field.getAttribute('aria-labelledby') || '').split(/\s+/).filter(Boolean);
        return ids.map((id) => this.formRoot.getElementById(id)?.textContent || '').join(' ') || field.name || '';
      }

      // Forms is a framework-controlled component, so set values through the
      // native setter and fire the events it listens for; assigning
      // field.value alone would be overwritten on its next render.
      setNativeValue(field, prototype, value) {
        if (field.value === value) return;
        Object.getOwnPropertyDescriptor(prototype, 'value').set.call(field, value);
        field.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
        field.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
      }
    }
  );
}
